import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'focus:ring-0 focus-visible:ring-0 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'border border-edge bg-paper-muted text-ink-muted hover:bg-paper-inset dark:bg-paper-muted dark:text-ink',
        primary: 'border border-sage bg-sage text-white hover:bg-sage-hover',
        danger:
          'border border-status-red-text bg-status-red-text text-white hover:bg-status-red-text/90',
        confirm: 'border border-sage bg-sage text-white hover:bg-sage-hover',
        dangerConfirm:
          'border border-status-red-text bg-status-red-text text-white hover:bg-status-red-text/90',
        neutral:
          'border border-edge bg-white text-ink-secondary hover:bg-paper-muted hover:text-ink',
        dropdown: 'bg-paper-inset text-ink-secondary rounded-none text-sm',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-edge bg-paper-light hover:bg-paper-inset hover:text-neutral-900 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:bg-neutral-800 dark:hover:text-neutral-50',
        secondary:
          'bg-paper-inset text-neutral-900 hover:bg-paper-inset/80 dark:bg-neutral-800 dark:text-neutral-50 dark:hover:bg-neutral-800/80',
        ghost:
          'hover:bg-paper-inset hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-50',
        link: 'text-neutral-900 underline-offset-4 hover:underline dark:text-neutral-50',
        custom: '',
      },
      size: {
        default: 'h-[28px] px-2 py-1',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
        workspace: 'h-8 min-h-8 rounded-md px-3 py-0 leading-none',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

type SemanticAction = {
  key?: string;
  label?: React.ReactNode;
  variant?: ButtonProps['variant'];
};

const getActionLabel = (label: React.ReactNode) =>
  typeof label === 'string' ? label.toLowerCase() : '';

export const getSemanticActionButtonVariant = (
  action: SemanticAction,
): ButtonProps['variant'] => {
  const key = String(action?.key || '').toLowerCase();
  const label = getActionLabel(action?.label);

  if (
    key === 'delete' ||
    key === 'refund' ||
    key === 'remove' ||
    key === 'disconnect' ||
    label.includes('deactivat') ||
    label.includes('delete') ||
    label.includes('refund') ||
    label.includes('remove') ||
    label.includes('disconnect') ||
    label.includes('cancel subscription')
  ) {
    return 'danger';
  }

  if (
    ['add', 'apply', 'connect', 'create', 'confirm', 'merge', 'publish', 'save', 'send', 'update', 'use'].includes(key) ||
    label.includes('add ') ||
    label.includes('apply') ||
    label.includes('connect') ||
    label.includes('activat') ||
    label.includes('create') ||
    label.includes('publish') ||
    label.includes('save') ||
    label.includes('update') ||
    label.includes('confirm') ||
    label.includes('merge') ||
    label.includes('send') ||
    label.includes('use')
  ) {
    return 'primary';
  }

  return action?.variant || 'neutral';
};

export const hasSemanticActionButtonVariant = (action: SemanticAction) =>
  ['primary', 'danger'].includes(String(getSemanticActionButtonVariant(action)));

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
